/**
 * The sidebar, as the October 2026 redesign specifies it (spec §2, prototype `k3-shell.js`).
 *
 * Role gating uses the same predicates as the backend (see `core/permissions.py`), so the
 * menu cannot offer a screen the API would refuse. The router applies the same roles to the
 * same paths (CLAUDE.md §7): a menu entry and its guard are changed together or not at all.
 *
 * Items whose screen another wave builds (Murojaatlar, Bronlar) carry their route already, so
 * the menu is complete now and the screen arrives with the merge.
 */
import {
  Briefcase,
  Building2,
  ChartColumn,
  Compass,
  House,
  Inbox,
  ListTodo,
  MessageSquare,
  Settings,
  Shuffle,
  Sparkles,
  Ticket,
  Users,
  Wallet,
  type LucideIcon,
} from "lucide-react";

import type { Role } from "@/api/types";
import type { TranslationKey } from "@/i18n";
import { canManageTenantSettings, canViewReports } from "@/lib/permissions";

export type NavGroup = "main" | "management";

/** The four sidebar numbers the navigation registry answers (`GET /api/nav/counts/`). */
export type NavCountKey = "murojaatlar" | "suhbatlar" | "bronlar" | "vazifalar";

export interface NavItem {
  key: string;
  labelKey: TranslationKey;
  group: NavGroup;
  icon: LucideIcon;
  /** `null` means every role with a membership. */
  roles: readonly Role[] | null;
  /** Route, or `null` when the screen is not in this phase. */
  to: string | null;
  /** The number shown beside the label, when this screen has one. */
  countKey?: NavCountKey;
}

const ALL_ROLES: readonly Role[] = ["operator", "branch_manager", "moliyachi", "owner"];
const REPORTING_ROLES: readonly Role[] = ALL_ROLES.filter(canViewReports);
const OWNER_ONLY: readonly Role[] = ALL_ROLES.filter(canManageTenantSettings);
/** Moliyachi has no free queue, conversations or bookings: those are the sales desk's screens. */
const SALES_DESK_ROLES: readonly Role[] = ["operator", "branch_manager", "owner"];
/** The AI assistant: whoever writes to clients. Moliyachi gets no entry (prototype `k3-shell.js`). */
const AI_ROLES: readonly Role[] = SALES_DESK_ROLES;
/** Moliya is hidden from an operator (prototype: `mol` excludes operator). */
const FINANCE_ROLES: readonly Role[] = ["branch_manager", "moliyachi", "owner"];
/** Boshqaruv: the managers and the owner (prototype `ADM`). Moliyachi reads reports only. */
const MANAGEMENT_ROLES: readonly Role[] = ["branch_manager", "owner"];
/** The one report an operator has: its own figures (TZ §17.11, `/reports/me`). */
const OPERATOR_ONLY: readonly Role[] = ["operator"];

export const NAV_ITEMS: readonly NavItem[] = [
  { key: "home", labelKey: "nav.home", group: "main", icon: House, roles: null, to: "/" },
  {
    key: "murojaatlar",
    labelKey: "nav.murojaatlar",
    group: "main",
    icon: Inbox,
    roles: SALES_DESK_ROLES,
    to: "/murojaatlar",
    countKey: "murojaatlar",
  },
  {
    key: "conversations",
    labelKey: "nav.conversations",
    group: "main",
    icon: MessageSquare,
    roles: SALES_DESK_ROLES,
    to: "/inbox",
    countKey: "suhbatlar",
  },
  { key: "deals", labelKey: "nav.deals", group: "main", icon: Briefcase, roles: null, to: "/bitimlar" },
  {
    key: "bronlar",
    labelKey: "nav.bronlar",
    group: "main",
    icon: Ticket,
    roles: SALES_DESK_ROLES,
    to: "/bronlar",
    countKey: "bronlar",
  },
  { key: "clients", labelKey: "nav.clients", group: "main", icon: Users, roles: null, to: "/mijozlar" },
  {
    key: "tourSearch",
    labelKey: "nav.tourSearch",
    group: "main",
    icon: Compass,
    roles: SALES_DESK_ROLES,
    to: "/tur-qidiruvi",
  },
  {
    key: "tasks",
    labelKey: "nav.tasks",
    group: "main",
    icon: ListTodo,
    roles: null,
    to: "/tasks",
    countKey: "vazifalar",
  },
  { key: "finance", labelKey: "nav.finance", group: "main", icon: Wallet, roles: FINANCE_ROLES, to: "/moliya" },
  {
    key: "aiAssistant",
    labelKey: "nav.aiAssistant",
    group: "main",
    icon: Sparkles,
    roles: AI_ROLES,
    to: "/yordamchi",
  },
  {
    key: "myReport",
    labelKey: "nav.myReport",
    group: "main",
    icon: ChartColumn,
    roles: OPERATOR_ONLY,
    to: "/hisobotlar/mening",
  },
  {
    key: "team",
    labelKey: "nav.team",
    group: "management",
    icon: Building2,
    roles: MANAGEMENT_ROLES,
    to: "/sozlamalar/jamoa",
  },
  {
    key: "distribution",
    labelKey: "nav.distribution",
    group: "management",
    icon: Shuffle,
    roles: MANAGEMENT_ROLES,
    to: "/sozlamalar/taqsimlash",
  },
  {
    // Reading the agency's contract wording is open to whoever issues contracts (D455);
    // only the owner's screen edits it. The menu follows the spec's Boshqaruv list.
    key: "contractTemplates",
    labelKey: "nav.contractTemplates",
    group: "management",
    icon: Briefcase,
    roles: MANAGEMENT_ROLES,
    to: "/shablonlar",
  },
  {
    key: "reports",
    labelKey: "nav.reports",
    group: "management",
    icon: ChartColumn,
    roles: REPORTING_ROLES,
    to: "/hisobotlar",
  },
  {
    key: "settings",
    labelKey: "nav.settings",
    group: "management",
    icon: Settings,
    roles: OWNER_ONLY,
    to: "/sozlamalar",
  },
];

export function visibleNavItems(role: Role): readonly NavItem[] {
  return NAV_ITEMS.filter((item) => item.roles === null || item.roles.includes(role));
}

export function navItemsForGroup(role: Role, group: NavGroup): readonly NavItem[] {
  return visibleNavItems(role).filter((item) => item.group === group);
}

/** How many destinations sit on the phone's bottom bar before the rest move under "Yana". */
export const MOBILE_TAB_COUNT = 4;

/**
 * Split the visible destinations into the bottom bar and the overflow sheet. Only items
 * with a route can sit on the bar: a later screen has nowhere to go to.
 */
export function mobileTabs(role: Role): { primary: readonly NavItem[]; overflow: readonly NavItem[] } {
  const routed = visibleNavItems(role).filter((item) => item.to !== null && item.group === "main");
  const primary = routed.slice(0, MOBILE_TAB_COUNT);
  const overflow = visibleNavItems(role).filter((item) => !primary.includes(item));
  return { primary, overflow };
}
